import PricingTableRow from "./PricingTableRow";
import { Refresh as ResetIcon } from "@mui/icons-material";
import {
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Alert,
  Stack,
  Autocomplete,
  TextField,
} from "@mui/material";
import * as dates from "common/dates";
import { toDecimalFromPercent, toPercentFromDecimal } from "common/numbers";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import Visible from "components/common/Visible";
import * as config from "config";
import * as labels from "config.labels";
import { connect } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as metaSelectors from "store/selectors/meta/metaSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const columns = [
  {
    name: "Expected Loss Cost",
    label: labels.PRICING_TABLE_EXPECTED_LOSS_COST_COLUMN,
    description: "The expected amount of annual loss.",
    key: "purePremium",
    prefix: "$",
    decimalScale: 0,
    isOverrideColumn: true,
    defaultVisible: true,
    visibilityKey: "purePremium",
  },
  {
    name: "Pure Premium",
    label: labels.PRICING_TABLE_PURE_PREMIUM_COLUMN,
    description:
      "The expected amount of premium required to cover average losses.",
    key: "purePremium",
    suffix: "%",
    decimalScale: 3,
    isOverrideColumn: true,
    defaultVisible: false,
    f: toPercentFromDecimal,
    inverseF: toDecimalFromPercent,
    visibilityKey: "purePremiumPercent",
  },
  {
    name: "Required 100% Premium",
    label: labels.PRICING_TABLE_REQUIRED_PREMIUM_COLUMN,
    description:
      "The amount of premium needed to be charged to cover the Expected Loss Cost after Brokerage, Other Acquisition Costs, Expenses and Profit.",
    key: "requiredPremium",
    prefix: "$",
    decimalScale: 0,
    defaultVisible: true,
    visibilityKey: "requiredPremium",
  },
  {
    name: "Required 100% Premium",
    label: labels.PRICING_TABLE_REQUIRED_PREMIUM_COLUMN,
    description:
      "The amount of premium needed to be charged to cover the average losses after Brokerage, Other Acquisition Costs, Expenses and Profit.",
    key: "requiredPremium",
    suffix: "%",
    decimalScale: 3,
    defaultVisible: false,
    f: toPercentFromDecimal,
    visibilityKey: "requiredPremiumPercent",
  },
  {
    name: "Target Price (TP)",
    label: labels.PRICING_TABLE_TARGET_PRICE_COLUMN,
    description: config.DEFINITION_TP,
    key: "targetPrice",
    suffix: "%",
    decimalScale: 2,
    goodScaler: (value) => (value ?? 100) - 100,
    defaultVisible: true,
    visibilityKey: "targetPrice",
  },
  {
    name: "Loss Ratio",
    label: labels.PRICING_TABLE_LOSS_RATIO_COLUMN,
    description: "Expected Loss Cost / 100% Premium",
    key: "lossRatio",
    suffix: "%",
    decimalScale: 2,
    defaultVisible: true,
    visibilityKey: "lossRatio",
  },
  {
    name: "GNULR",
    label: labels.PRICING_TABLE_GNULR_COLUMN,
    description: config.DEFINITION_GNULR,
    key: "technicalRatio",
    suffix: "%",
    decimalScale: 2,
    defaultVisible: true,
    visibilityKey: "technicalRatio",
  },
];

export const PricingTableComponent = ({
  layer,
  analyticsConfig,
  modelConfig,
  prices,
  pricingError,
  selectedPrice,
  setSelectedModel,
  setSelectedPrice,
  refreshPricing,
  isReadOnly = false,
  columns,
  targetPerspective = dates.ProratedPerspective.PRORATED,
  proRataFactor = 1,
}) => {
  const models = analyticsConfig?.layerPricing?.models ?? [];
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const selectedModel = layer.pricing?.selectedModel;

  return (
    <Grid container spacing={config.GRID_SPACING}>
      {models.length > 1 && (
        <Grid item container xs={12} justifyContent={"right"}>
          <Autocomplete
            options={models}
            getOptionLabel={({ name }) => name}
            value={
              selectedModel == null
                ? { key: "__UNSET__", name: "" }
                : models.find(
                    (model) => model.key === layer.pricing?.selectedModel
                  ) ?? { key: selectedModel, name: "[Unknown]" }
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant={"filled"}
                label={translateLabel(labels.LAYER_PRICING_MODEL_SELECT_LABEL, {
                  default: "Pricing Model",
                })}
                InputLabelProps={{
                  ...params?.InputLabelProps,
                  shrink: true,
                }}
                InputProps={{
                  ...params?.InputProps,
                  spellCheck: "false",
                }}
              />
            )}
            onChange={(_, value) => {
              const model = value == null ? null : value.key;
              setSelectedModel({ model });
              // A layer priced before this model was configured has no prices for it, and the
              // layer resolver then falls back to the default model's prices, so price it now
              if (
                model != null &&
                layer.pricing?.prices &&
                !(model in layer.pricing.prices) &&
                !isReadOnly
              ) {
                refreshPricing();
              }
            }}
            size={"small"}
            sx={{ minWidth: "20rem" }}
          />
        </Grid>
      )}
      <Visible
        byTag={"pricing.layerPricing.nonDefaultModelWarning"}
        defaultVisible={false}
      >
        <Grid item xs={12}>
          {selectedModel &&
            layer.pricing?.defaultModel &&
            selectedModel !== layer.pricing.defaultModel && (
              <Alert
                severity={"info"}
                fullWidth
                action={
                  <IconButton onClick={() => setSelectedModel({ model: null })}>
                    <ResetIcon fontSize={"small"} />
                  </IconButton>
                }
              >
                {translateLabel(
                  labels.LAYER_PRICING_NON_DEFAULT_MODEL_MESSAGE,
                  {
                    default:
                      "The selected pricing model is different from the default.",
                  }
                )}
              </Alert>
            )}
        </Grid>
      </Visible>
      <Grid item xs={12}>
        <TableContainer>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((column, i) => {
                  return (
                    <Tooltip key={i} title={column.description}>
                      <TableCell align={"right"}>
                        {column.label
                          ? translateLabel(column.label, {
                              default: column.name,
                            })
                          : column.name}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {(modelConfig?.measures ?? [])
                .filter(
                  (measureConfig) =>
                    !measureConfig.excludeIfEmpty ||
                    Object.keys(prices?.[measureConfig.key] ?? {}).length > 0
                )
                .map((measureConfig) => {
                  return (
                    <PricingTableRow
                      key={measureConfig.key}
                      meta={measureConfig}
                      values={adjustValuesByPerspective(
                        prices[measureConfig.key],
                        targetPerspective,
                        proRataFactor
                      )}
                      columns={columns}
                    />
                  );
                })}
              {modelConfig?.selectedPrice?.isVisible && (
                <PricingTableRow
                  meta={modelConfig.selectedPrice}
                  values={adjustValuesByPerspective(
                    selectedPrice,
                    targetPerspective,
                    proRataFactor
                  )}
                  columns={columns}
                  onChangeNumber={
                    modelConfig.selectedPrice.isEditable
                      ? (value) =>
                          setSelectedPrice({
                            model: modelConfig.key,
                            value: adjustValueForStorage(
                              value,
                              targetPerspective,
                              proRataFactor
                            ),
                          })
                      : undefined
                  }
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid container item xs={12} justifyContent={"flex-end"}>
        <Stack
          direction={"column"}
          spacing={config.GRID_SPACING}
          sx={{ width: "100%", marginTop: config.GRID_SPACING }}
          alignItems={"flex-end"}
        >
          {pricingError && (modelConfig?.showErrors ?? true) && (
            <Alert severity={"error"} sx={{ width: "100%" }}>
              {pricingError}
            </Alert>
          )}
          <Disabled ifReadOnly>
            <div data-testid={"calculate-pricing"}>
              <Button
                onClick={refreshPricing}
                isLoading={layer.loadingPurePremium}
                className={"button-fixed-width"}
                data-testid={"calculate-pricing-button"}
              >
                {"Calculate"}
              </Button>
            </div>
          </Disabled>
        </Stack>
      </Grid>
    </Grid>
  );
};

export const mapStateToProps = (state) => {
  const userConfig = userSelectors.selectUserConfig(state);
  const isColumnVisible = (column) => {
    return (
      userConfig.componentVisibility?.[
        "pricing.layerPricing.pricingTable." + column.visibilityKey
      ] ?? column.defaultVisible
    );
  };
  const layer = pricingSelectors.getActiveLayer(state) ?? {};
  const modelConfig = pricingSelectors.selectActiveLayerPricingSelectedModelConfig(
    state
  );
  return {
    columns: columns.filter(isColumnVisible),
    layer,
    analyticsConfig: staticDataSelectors.selectAnalyticsConfig(state),
    modelConfig,
    prices: pricingSelectors.selectActiveLayerPrices(state),
    pricingError: layer?.pricing?.prices?.[modelConfig?.key]?.error,
    selectedPrice: pricingSelectors.selectActiveLayerSelectedPrice(state),
    isReadOnly:
      userSelectors.isUserReadOnly(state) ||
      metaSelectors.selectOverlay(state) != null,
  };
};

// Adjust values based on the selected perspective (annualized or pro-rated)
const adjustValuesByPerspective = (
  values,
  targetPerspective,
  proRataFactor
) => {
  if (!values || proRataFactor === 1) {
    return values;
  }

  // Create a copy of the values object to avoid modifying the original
  const adjustedValues = { ...values };

  const metricsToProrate = [
    "purePremium",
    "requiredPremium",
    "selectedPremium",
  ];
  // These are ratios of values stored with different perspectives so always prorate
  // regardless of the output perspective.
  const metricsToScale = ["targetPrice", "lossRatio", "technicalRatio"];

  // Apply adjustments based on perspective
  Object.keys(adjustedValues).forEach((metric) => {
    if (typeof adjustedValues[metric] !== "number") {
      return;
    }
    if (metricsToScale.includes(metric)) {
      adjustedValues[metric] = adjustedValues[metric] * proRataFactor;
    } else if (metricsToProrate.includes(metric)) {
      if (targetPerspective === dates.ProratedPerspective.PRORATED) {
        adjustedValues[metric] = adjustedValues[metric] * proRataFactor;
      }
    }
  });

  return adjustedValues;
};

// Adjust value for storage when user edits a field
const adjustValueForStorage = (value, targetPerspective, proRataFactor) => {
  // Handle null/undefined values - preserve them as is when deleting
  if (value === null || value === undefined) {
    return value;
  }

  if (
    proRataFactor === 1 ||
    targetPerspective === dates.ProratedPerspective.ANNUALIZED
  ) {
    return value; // No adjustment needed
  }

  // Convert from pro-rated to annualized for storage
  return proRataFactor ? value / proRataFactor : null;
};

const mapDispatchToProps = {
  setSelectedModel: pricingActions.setActiveLayerPricingSelectedModel,
  setSelectedPrice: pricingActions.setActiveLayerPricingSelectedPrice,
  refreshPricing: pricingActions.refreshActiveLayerPricing,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PricingTableComponent);
